"use client";

import clsx from "clsx";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import Image from "next/image";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import Icon, { ICON_SIZES } from "@/components/library/Icon";
import { Button } from "@/components/ui/button";
import type { FeatureAnnouncementMedia } from "@/types/featureAnnouncement";
import { FLOATING_SURFACE_BOTTOM_PX } from "@/utils/common/floatingClearance";

interface FeatureAnnouncementCardProps {
  isVisible: boolean;
  title: string;
  description: string;
  primaryLabel: string;
  onPrimary: () => void;
  onDismiss: () => void;
  media?: FeatureAnnouncementMedia;
  /** Distance from the viewport bottom, e.g. to clear a sticky footer. */
  bottomOffset?: number;
}

export default function FeatureAnnouncementCard({
  isVisible,
  title,
  description,
  primaryLabel,
  onPrimary,
  onDismiss,
  media,
  bottomOffset = FLOATING_SURFACE_BOTTOM_PX,
}: Readonly<FeatureAnnouncementCardProps>) {
  const { t } = useTranslation("common");
  const reduceMotion = useReducedMotion() ?? false;

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      onDismiss();
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.aside
          initial={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          style={{ bottom: bottomOffset }}
          className={clsx(
            "fixed",
            "right-6",
            "z-50",
            "flex",
            "w-[340px]",
            "max-w-[calc(100vw-3rem)]",
            "flex-col",
            "gap-4",
            "rounded-2xl",
            "bg-panel",
            "p-4",
            "font-monument",
            "tracking-[-0.005em]",
            "antialiased",
            "shadow-floating"
          )}
          aria-label={t("whatsNew")}
          onKeyDown={handleKeyDown}
        >
          {media && (
            <div
              className={clsx(
                "relative",
                "aspect-[16/10]",
                "w-full",
                "overflow-hidden",
                "rounded-lg",
                "bg-[#E4E8F8]",
                "dark:bg-100"
              )}
            >
              {media.type === "video" ? (
                <video
                  src={media.src}
                  poster={media.poster}
                  className="h-full w-full object-cover"
                  autoPlay={!reduceMotion}
                  loop
                  muted
                  playsInline
                  aria-hidden
                />
              ) : (
                <Image
                  src={media.src}
                  alt=""
                  fill
                  sizes="340px"
                  className="object-cover object-left-top"
                  unoptimized={/^https?:\/\//.test(media.src)}
                />
              )}
            </div>
          )}

          <Button
            variant="ghost"
            size="icon"
            aria-label={t("close")}
            onClick={onDismiss}
            className={clsx(
              "absolute",
              "right-6",
              "top-6",
              "h-6",
              "w-6",
              "rounded-full",
              "bg-panel/90",
              "text-900",
              "hover:bg-panel",
              "shadow-hairline"
            )}
          >
            <Icon icon="xmark" size={ICON_SIZES.XS} />
          </Button>

          <div className={clsx("flex", "flex-col", "gap-3.5", "px-1", "pb-1")}>
            <div className={clsx("flex", "flex-col", "gap-1", !media && "pr-8")}>
              <h3
                className={clsx(
                  "text-[17px]",
                  "font-medium",
                  "leading-[22px]",
                  "tracking-[-0.02em]",
                  "text-900",
                  "[text-wrap:pretty]"
                )}
              >
                {title}
              </h3>
              <p className={clsx("text-[13px]", "leading-[18px]", "text-500")}>{description}</p>
            </div>
            <div className={clsx("flex", "items-center", "justify-between")}>
              <Button
                variant="outline"
                className={clsx(
                  "h-auto",
                  "rounded-full",
                  "px-3.5",
                  "py-[7px]",
                  "text-[13px]",
                  "leading-[18px]"
                )}
                onClick={onPrimary}
              >
                {primaryLabel}
              </Button>
              <Button
                variant="link"
                className={clsx(
                  "h-auto",
                  "p-0",
                  "text-[13px]",
                  "leading-[18px]",
                  "hover:no-underline",
                  "hover:opacity-80"
                )}
                onClick={onDismiss}
              >
                {t("featureAnnouncement.notNow")}
              </Button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
